/**
 * Integration Services presentation adapter for Operational Readiness.
 * Maps subscription diagnostics / lists + vision capabilities — no invented probes.
 *
 * Phase 18: Provider Available means platform-licensed Vision Providers
 * (PlatformVisionProviderConfig), not Technology Partner (WebhookProvider) count.
 * Event subscriptions become required only when cameras exist and analytics that
 * need provider event delivery are enabled.
 */

import {VisionContext} from '../../../models/vte';
import {VisionProviderDiagnostics} from '../../../models/vision-provider-management';
import {
    hasVisionAnalytics,
    isVehicleDetectionAnalyticsKey,
} from '../../../services/vte/vision-customer-labels';
import {OperationalStatusCardView, OperationalWarningView,} from '../operational-readiness.models';
import {CameraReadinessRow} from './shared';

const V3_LPR_CAPABILITY = 'eagle_eye_v3_lpr_detection';

export type IntegrationSubscriptionRow = {
    subscription_id?: string;
    status?: string;
    organization_id?: number | null;
    camera_ids?: string[];
    event_types?: string[];
};

export type IntegrationServicesAdapterInput = {
    organizationId: number;
    visionContext: VisionContext | null;
    cameras: CameraReadinessRow[] | null;
    diagnostics: VisionProviderDiagnostics | null;
    subscriptions: IntegrationSubscriptionRow[] | null;
};

function readyCard(
    key: string,
    title: string,
    detail?: string,
): OperationalStatusCardView {
    return {
        key,
        title,
        status: 'ready',
        statusLabel: 'Ready',
        detail,
    };
}

function informationalCard(
    key: string,
    title: string,
    detail: string,
    statusLabel = 'Not required',
): OperationalStatusCardView {
    return {
        key,
        title,
        status: 'informational',
        statusLabel,
        detail,
    };
}

function pendingCard(
    key: string,
    title: string,
    warning: OperationalWarningView,
    status: 'missing' | 'warning' | 'blocked' = 'missing',
    statusLabel = 'Pending',
    detail?: string,
): OperationalStatusCardView {
    return {
        key,
        title,
        status,
        statusLabel,
        detail,
        warnings: [warning],
    };
}

function unknownCard(key: string, title: string, detail: string): OperationalStatusCardView {
    return {
        key,
        title,
        status: 'unknown',
        statusLabel: 'Unknown',
        detail,
    };
}

/**
 * Organization vision capabilities that imply provider event subscriptions
 * once cameras have matching enabled analytics.
 */
export function eventSubscriptionsRequired(capabilities: string[]): boolean {
    const caps = capabilities || [];
    if (caps.includes(V3_LPR_CAPABILITY)) {
        return true;
    }
    return hasVisionAnalytics(caps);
}

function anyLoadedCameraNeedsSubscriptions(cameras: CameraReadinessRow[]): boolean {
    return cameras.some((camera) => {
        const enabled = camera.status?.enabled_analytics || [];
        if (enabled.length > 0) {
            return enabled.some((item) => {
                const key = item.analytics_key || '';
                return isVehicleDetectionAnalyticsKey(key) || hasVisionAnalytics([key]);
            });
        }
        return Boolean(camera.status?.vehicle_detection_enabled);
    });
}

function activeOrgSubscriptions(
    subscriptions: IntegrationSubscriptionRow[],
    organizationId: number,
): IntegrationSubscriptionRow[] {
    return subscriptions.filter((row) => {
        const status = String(row.status || '').toUpperCase();
        const isActive = !status || status === 'ACTIVE';
        return row.organization_id === organizationId && isActive;
    });
}

export function mapIntegrationServicesCards(
    input: IntegrationServicesAdapterInput,
): OperationalStatusCardView[] {
    const {
        organizationId,
        visionContext,
        cameras,
        diagnostics,
        subscriptions,
    } = input;
    const cards: OperationalStatusCardView[] = [];

    const supportedFromDiagnostics = diagnostics?.supported_providers || [];
    const supportedFromContext = (visionContext?.supported_providers || [])
        .map((provider) => provider.provider_id)
        .filter(Boolean);
    const supportedProviders = [
        ...new Set([...supportedFromDiagnostics, ...supportedFromContext]),
    ];

    // 1. Vision Providers Available (platform registry — not Technology Partners)
    if (diagnostics === null && visionContext === null) {
        cards.push(
            unknownCard(
                'provider_available',
                'Vision Providers Available',
                'Platform vision provider availability could not be loaded.',
            ),
        );
    } else if (supportedProviders.length > 0) {
        cards.push(
            readyCard(
                'provider_available',
                'Vision Providers Available',
                `${supportedProviders.length} platform vision provider(s) licensed for selection. ` +
                'This is not the Technology Partner (webhook provider) catalog.',
            ),
        );
    } else {
        cards.push(
            pendingCard(
                'provider_available',
                'Vision Providers Available',
                {
                    problem: 'No platform vision providers are licensed/active.',
                    impact: 'Organization vision capabilities and event subscriptions cannot be configured.',
                    recommendedAction:
                        'Ensure at least one vision provider is active in platform vision provider configuration.',
                    scope: 'platform',
                },
            ),
        );
    }

    // 2. Provider Authentication
    if (diagnostics === null) {
        cards.push(
            unknownCard(
                'provider_authentication',
                'Provider Authentication',
                'Provider authentication status could not be loaded.',
            ),
        );
    } else if (diagnostics.oauth_configured) {
        const expiry = diagnostics.oauth_token_expires_at
            ? ` Token expiry: ${diagnostics.oauth_token_expires_at}.`
            : '';
        cards.push(
            readyCard(
                'provider_authentication',
                'Provider Authentication',
                `Provider authentication is configured.${expiry}`,
            ),
        );
    } else {
        cards.push(
            pendingCard(
                'provider_authentication',
                'Provider Authentication',
                {
                    problem: 'Provider authentication is not configured.',
                    impact: 'Event subscriptions and provider APIs that require authentication will fail.',
                    recommendedAction:
                        'Complete provider authentication setup for this platform, then refresh diagnostics.',
                    scope: 'platform',
                },
                'missing',
                'Pending',
            ),
        );
    }

    // 3. Event Subscriptions
    const capabilities = visionContext?.vision_policy?.vision_capabilities || [];
    const policyRequiresSubscriptions = eventSubscriptionsRequired(capabilities);
    const hasCameras = (cameras?.length || 0) > 0;
    const analyticsEligible = anyLoadedCameraNeedsSubscriptions(cameras || []);

    if (!policyRequiresSubscriptions) {
        cards.push(
            informationalCard(
                'event_subscriptions',
                'Event Subscriptions',
                'Not required for the current organization vision capabilities.',
            ),
        );
        return cards;
    }

    if (cameras === null) {
        cards.push(
            unknownCard(
                'event_subscriptions',
                'Event Subscriptions',
                'Camera list could not be loaded to evaluate subscription requirements.',
            ),
        );
        return cards;
    }

    if (!hasCameras) {
        cards.push(
            informationalCard(
                'event_subscriptions',
                'Event Subscriptions',
                'Not required until cameras are registered for this organization.',
            ),
        );
        return cards;
    }

    if (!analyticsEligible) {
        cards.push(
            pendingCard(
                'event_subscriptions',
                'Event Subscriptions',
                {
                    problem:
                        'Cameras are registered, but required analytics are not enabled on any loaded camera.',
                    impact:
                        'Provider event subscriptions are only required after vehicle detection or vision analytics are enabled.',
                    recommendedAction:
                        'Enable vehicle detection (and any required vision analytics) on the camera, then create subscriptions.',
                    scope: 'organization',
                    condition: 'Requires enabled analytics that need provider event delivery',
                },
            ),
        );
        return cards;
    }

    if (subscriptions === null && diagnostics === null) {
        cards.push(
            unknownCard(
                'event_subscriptions',
                'Event Subscriptions',
                'Event subscription status could not be loaded.',
            ),
        );
        return cards;
    }

    const orgActiveCount =
        subscriptions === null
            ? null
            : activeOrgSubscriptions(subscriptions, organizationId).length;

    if (orgActiveCount != null && orgActiveCount > 0) {
        cards.push(
            readyCard(
                'event_subscriptions',
                'Event Subscriptions',
                `${orgActiveCount} active subscription(s) for this organization.`,
            ),
        );
        return cards;
    }

    if (orgActiveCount == null && (diagnostics?.active_local_subscription_count || 0) > 0) {
        cards.push(
            readyCard(
                'event_subscriptions',
                'Event Subscriptions',
                'Active provider subscriptions exist on the platform.',
            ),
        );
        return cards;
    }

    cards.push(
        pendingCard(
            'event_subscriptions',
            'Event Subscriptions',
            {
                problem: 'Required event subscription is missing for the configured camera(s).',
                impact: 'Provider events required by enabled analytics may not be delivered.',
                recommendedAction:
                    'Open Provider Management and create or reconcile event subscriptions for this organization’s cameras.',
                scope: 'organization',
                condition: 'Required because enabled camera analytics need provider event delivery',
            },
            'warning',
            'Warning',
        ),
    );

    return cards;
}
